import type { ReactNode } from 'react';
import { Card } from './Card';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: ReactNode;
  trend?: {
    value: number;
    label: string;
  };
  variant?: 'default' | 'accent' | 'dark';
  className?: string;
}

export function StatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  variant = 'default',
  className = '',
}: StatCardProps) {
  const isPositive = trend && trend.value >= 0;

  const variantStyles = {
    default: {
      card: '',
      title: 'text-[var(--asi-text-secondary)]',
      value: 'text-[var(--asi-text)]',
      subtitle: 'text-[var(--asi-text-muted)]',
      icon: 'bg-[var(--asi-bg-tertiary)] text-[var(--asi-text)]',
    },
    accent: {
      card: 'bg-[var(--asi-accent-light)]',
      title: 'text-[var(--asi-text-secondary)]',
      value: 'text-[var(--asi-text)]',
      subtitle: 'text-[var(--asi-text-secondary)]',
      icon: 'bg-[var(--asi-accent)] text-[var(--asi-text)]',
    },
    dark: {
      card: 'bg-[var(--asi-primary)]',
      title: 'text-[var(--asi-text-light-secondary)]',
      value: 'text-[var(--asi-text-light)]',
      subtitle: 'text-[var(--asi-text-light-secondary)]',
      icon: 'bg-[var(--asi-accent)] text-[var(--asi-text)]',
    },
  };

  const styles = variantStyles[variant];

  return (
    <Card
      className={`overflow-hidden ${styles.card} ${className}`}
      variant={variant === 'dark' ? 'dark' : 'default'}
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${styles.title}`}>{title}</p>
            <p className={`mt-2 text-3xl font-semibold tracking-tight ${styles.value}`}>
              {value}
            </p>
            {subtitle && (
              <p className={`mt-1 text-sm ${styles.subtitle}`}>{subtitle}</p>
            )}
            {trend && (
              <div className="mt-2 flex items-center gap-1">
                <span
                  className={`inline-flex items-center gap-0.5 text-sm font-medium ${
                    isPositive ? 'text-[var(--asi-success)]' : 'text-[var(--asi-error)]'
                  }`}
                >
                  {isPositive ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  {Math.abs(trend.value)}%
                </span>
                <span className={`text-sm ${styles.subtitle}`}>{trend.label}</span>
              </div>
            )}
          </div>
          {icon && (
            <div className={`flex-shrink-0 p-3 rounded-xl ${styles.icon}`}>
              {icon}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// Mini stat for compact displays
interface MiniStatProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  className?: string;
}

export function MiniStat({ label, value, icon, className = '' }: MiniStatProps) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {icon && (
        <div className="p-2 rounded-lg bg-[var(--asi-bg-tertiary)] text-[var(--asi-text-secondary)]">
          {icon}
        </div>
      )}
      <div>
        <p className="text-sm font-medium text-[var(--asi-text)]">{value}</p>
        <p className="text-xs text-[var(--asi-text-muted)]">{label}</p>
      </div>
    </div>
  );
}
